import axios from 'axios';
import { API_BASE_URL } from '../../config';

/** Every job management endpoint lives under /api/jm. */
const JM = `${API_BASE_URL}/jm`;

/**
 * Resumes are served through the API rather than linked at directly, so the
 * download keeps its filename and a missing file reports itself instead of
 * silently 404-ing as HTML.
 */
export const resumeUrl = (name, { download = false } = {}) => {
  if (!name) return null;
  if (/^https?:\/\//i.test(name)) return name;

  const file = encodeURIComponent(name.split('/').pop());
  return `${JM}/resume?file=${file}${download ? '&download=1' : ''}`;
};

/** The API root without /api — candidate photos are served straight from there. */
const FILE_ROOT = API_BASE_URL.replace(/\/api\/?$/, '');

/**
 * A candidate's photo. Records carried over from the old app store a bare
 * filename under uploads/profile, so one rule covers old and new alike.
 */
export const photoUrl = (name) => {
  if (!name) return null;
  if (/^https?:\/\//i.test(name)) return name;
  if (name.includes('/')) return `${FILE_ROOT}/${name}`;
  return `${FILE_ROOT}/uploads/profile/${name}`;
};

/** Who is acting — every stage write records this as done_by / rejected_by / hold_by. */
export const actingUser = () => ({
  employee_id: localStorage.getItem('employee_id') || '',
  user_id: localStorage.getItem('user_id') || '',
});

const get = (path, params) => axios.get(`${JM}${path}`, { params }).then((r) => r.data);

/** Stage writes carry file inputs, so they always go up as multipart form data. */
const postForm = (path, payload) => {
  const form = new FormData();

  Object.entries({ ...actingUser(), ...payload }).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    form.append(key, value);
  });

  return axios.post(`${JM}${path}`, form).then((r) => r.data);
};

const jm = {
  // masters
  jobs: () => get('/jobs'),
  employees: () => get('/employees'),
  departments: () => get('/departments'),
  designations: () => get('/designations'),
  companies: () => get('/companies'),
  jobTypes: () => get('/job-types'),
  shifts: () => get('/shifts'),
  contractors: () => get('/contractors'),
  contractTypes: () => get('/contract-types'),

  // job posts
  createJob: (job) => axios.post(`${JM}/job-posts`, job).then((r) => r.data),
  updateJob: (id, job) => axios.put(`${JM}/job-posts/${id}`, job).then((r) => r.data),
  deleteJob: (id) => axios.delete(`${JM}/job-posts/${id}`).then((r) => r.data),

  // one candidate across every stage — powers all the edit dialogs
  candidate: (id) => get(`/candidate/${id}`),

  // tab lists
  applications: () => get('/applications'),
  shortlisted: () => get('/shortlisted'),
  demo: () => get('/demo'),
  interviews: () => get('/interviews'),
  final: () => get('/final'),
  recruitment: () => get('/recruitment'),
  hold: () => get('/hold'),
  rejected: () => get('/rejected'),
  report: (monthYear, status) => get('/report', { month_year: monthYear, status }),
  search: (input) => get('/search', { input }),

  // stage writes
  addCandidate: (payload) => postForm('/applications', payload),
  deleteCandidate: (id) => axios.delete(`${JM}/applications/${id}`).then((r) => r.data),
  saveApplication: (id, payload) => postForm(`/applications/${id}`, payload),
  saveShortlisted: (id, payload) => postForm(`/shortlisted/${id}`, payload),
  saveDemo: (id, payload) => postForm(`/demo/${id}`, payload),
  saveInterview: (id, payload) => postForm(`/interviews/${id}`, payload),
  saveFinal: (id, payload) => postForm(`/final/${id}`, payload),
  saveRecruitment: (id, payload) => postForm(`/recruitment/${id}`, payload),
  saveHold: (id, payload) => postForm(`/hold/${id}`, payload),
};

export default jm;

/** Turn any axios failure into the message the API meant to show. */
export const errorMessage = (error, fallback = 'Something went wrong') =>
  error?.response?.data?.error || error?.message || fallback;

export const fullName = (row) =>
  [row?.first_name, row?.last_name].filter(Boolean).join(' ') || '—';

/**
 * Who referred the candidate. An employee referral resolves to
 * "Name (code)"; otherwise the free-text "Referred_others" stands in.
 */
export const referredBy = (row) => {
  if (row?.refered_by && row?.ref_first_name) {
    const name = [row.ref_first_name, row.ref_last_name].filter(Boolean).join(' ');
    return `${name} (${row.ref_emp_code})`;
  }
  return row?.Referred_others || '—';
};

/** yyyy-mm-dd — the format every other screen in the HRMS shows. */
export const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-CA');
};

export const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.toLocaleDateString('en-CA')} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** yyyy-mm-dd for date inputs, from whatever shape the API returned. */
export const toInputDate = (value) => {
  if (!value) return '';
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};
